import { useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL, CATEGORIES, CATEGORY_NAME, fmtDuration } from "@/lib/api";
import { Search, X, Play } from "lucide-react";
import { toast } from "sonner";

/**
 * Mobile Library — search + category filter + list of clips with
 * tap-to-preview full-screen. Read-only; edits happen on desktop.
 */
export default function MobileLibrary() {
  const [clips, setClips] = useState([]);
  const [videosById, setVideosById] = useState({});
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, v] = await Promise.all([
          api.get("/clips"),
          api.get("/videos"),
        ]);
        setClips(c.data || []);
        const m = {};
        for (const vid of v.data || []) m[vid.id] = vid;
        setVideosById(m);
      } catch (err) {
        toast.error("Failed to load clips");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return clips.filter((c) => {
      if (cat && c.category !== cat) return false;
      if (!query) return true;
      const ev = videosById[c.video_id]?.event_name || "";
      return (
        (c.rider_horse_name || "").toLowerCase().includes(query) ||
        (c.horse_name || "").toLowerCase().includes(query) ||
        (c.heeler_name || "").toLowerCase().includes(query) ||
        (c.sub_label || "").toLowerCase().includes(query) ||
        ev.toLowerCase().includes(query)
      );
    });
  }, [clips, q, cat, videosById]);

  return (
    <div className="px-4 pt-4" data-testid="mobile-library-page">
      {/* Search */}
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
        <input
          className="field-input !pl-9 !py-3"
          placeholder="Search rider, horse, event…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="mobile-search"
        />
        {q && (
          <button
            onClick={() => setQ("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-200"
            data-testid="mobile-search-clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Category chips */}
      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3 -mx-4 px-4 scrollbar-hide">
        <button
          onClick={() => setCat("")}
          className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border whitespace-nowrap ${
            !cat ? "bg-orange-600 border-orange-500 text-white" : "border-stone-700 text-stone-400"
          }`}
          data-testid="mobile-cat-all"
        >
          All
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setCat(c.id === cat ? "" : c.id)}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border whitespace-nowrap ${
              cat === c.id ? "bg-orange-600 border-orange-500 text-white" : "border-stone-700 text-stone-400"
            }`}
            data-testid={`mobile-cat-${c.id}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Clip list */}
      {loading ? (
        <div className="text-center text-stone-500 py-16 animate-pulse text-xs uppercase tracking-widest">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-stone-500 py-16 text-xs">No clips match.</div>
      ) : (
        <div className="space-y-2 pb-4" data-testid="mobile-clip-list">
          {filtered.map((clip) => {
            const ev = videosById[clip.video_id]?.event_name || "";
            const rider = clip.heeler_name
              ? `${clip.rider_horse_name || "—"} & ${clip.heeler_name}`
              : clip.rider_horse_name;
            return (
              <button
                key={clip.id}
                onClick={() => setPreview(clip)}
                className="w-full flex items-center gap-3 border border-stone-800 hover:border-orange-600/60 bg-stone-950/60 p-2 text-left active:scale-[0.98] transition-transform"
                data-testid={`mobile-clip-${clip.id}`}
              >
                <div className="relative w-24 h-14 bg-stone-900 shrink-0 overflow-hidden">
                  {clip.thumbnail_s3_key ? (
                    <img
                      src={`${BACKEND_URL}/api/clips/${clip.id}/thumbnail`}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-700 text-[9px]">no thumb</div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100">
                    <Play size={20} className="text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-orange-400/80 truncate">
                    {CATEGORY_NAME[clip.category] || clip.category}
                    {clip.sub_label && <span className="text-stone-500"> · {clip.sub_label}</span>}
                  </div>
                  <div className="text-sm font-medium text-stone-100 truncate">
                    {rider || "Untagged"}
                  </div>
                  <div className="text-[10px] text-stone-500 truncate">
                    {ev} {clip.horse_name && `· ${clip.horse_name}`}
                  </div>
                </div>
                <div className="text-[10px] text-stone-500 font-mono-tc shrink-0">
                  {fmtDuration(clip.duration)}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Fullscreen preview */}
      {preview && (
        <div
          className="fixed inset-0 bg-black z-50 flex flex-col"
          onClick={() => setPreview(null)}
          data-testid="mobile-preview"
        >
          <div className="flex items-center justify-between px-4 py-3 text-stone-100 bg-black/80" onClick={(e) => e.stopPropagation()}>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-orange-400/80">
                {CATEGORY_NAME[preview.category] || preview.category}
              </div>
              <div className="text-sm truncate">
                {preview.rider_horse_name || preview.sub_label || "Clip"}
              </div>
            </div>
            <button
              onClick={() => setPreview(null)}
              className="text-stone-300 hover:text-white"
              data-testid="mobile-preview-close"
            >
              <X size={20} />
            </button>
          </div>
          <video
            src={`${BACKEND_URL}/api/clips/${preview.id}/stream`}
            controls
            autoPlay
            playsInline
            className="flex-1 w-full bg-black"
            data-testid="mobile-preview-video"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
